import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail, Send, Clock, Users, FileText, Plus, Trash2, Edit,
  Loader2, RefreshCw, Eye, Calendar, CheckCircle, XCircle,
  ChevronRight, Save, X, Copy, AlertTriangle, User
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';

export function AdminNewsletterTab() {
  const [activeView, setActiveView] = useState('compose'); // compose, templates, campaigns, lists
  const [loading, setLoading] = useState(true);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    html_body: '',
    category: 'general'
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [visualEditorMode, setVisualEditorMode] = useState(false);
  const [detectedPlaceholders, setDetectedPlaceholders] = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [recipientLists, setRecipientLists] = useState([]);
  
  // Custom Mailing Lists
  const [customLists, setCustomLists] = useState([]);
  const [customListDialog, setCustomListDialog] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [customListForm, setCustomListForm] = useState({
    name: '',
    description: '',
    emails_text: '' // One email per line with optional name: "email, name"
  });
  const [savingList, setSavingList] = useState(false);
  
  // Weekend Event Campaigns
  const [weekendEvents, setWeekendEvents] = useState([]);
  const [selectedWeekendEvent, setSelectedWeekendEvent] = useState('');
  const [weekendRecipientCount, setWeekendRecipientCount] = useState(0);
  const [sendingWeekendCampaign, setSendingWeekendCampaign] = useState(false);
  
  // Individual Send (for verified financial emails)
  const [individualMode, setIndividualMode] = useState(false);
  const [individualRecipient, setIndividualRecipient] = useState({
    email: '',
    name: '',
    first_name: '',
    booking_reference: '',
    booking_total: '',
    deposit_paid: '',
    instalments_paid: '',
    balance_remaining: '',
    payment_deadline: '',
    booking_includes: ''
  });
  const [sendingIndividual, setSendingIndividual] = useState(false);
  
  // Compose
  const [composeForm, setComposeForm] = useState({
    name: '',
    subject: '',
    html_body: '',
    recipient_list_id: '',
    manual_recipients: '',
    schedule: false,
    scheduled_at: '',
    include_dyslexia_pdf: false
  });
  const [sending, setSending] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load templates
      const templatesRes = await fetch(`${API_URL}/api/newsletter/admin/templates`, {
        headers: getAuthHeaders()
      });
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }

      // Load campaigns
      const campaignsRes = await fetch(`${API_URL}/api/newsletter/admin/campaigns`, {
        headers: getAuthHeaders()
      });
      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setCampaigns(data.campaigns || []);
      }

      // Load recipient lists
      const listsRes = await fetch(`${API_URL}/api/membership/admin/recipient-lists`, {
        headers: getAuthHeaders()
      });
      if (listsRes.ok) {
        const data = await listsRes.json();
        setRecipientLists(data.lists || []);
      }

      // Load custom mailing lists
      const customRes = await fetch(`${API_URL}/api/newsletter/admin/custom-lists`, {
        headers: getAuthHeaders()
      });
      if (customRes.ok) {
        const data = await customRes.json();
        setCustomLists(data.lists || []);
      }

      // Load weekend events for event-specific campaigns
      const weekendRes = await fetch(`${API_URL}/api/newsletter/admin/weekend-events`, {
        headers: getAuthHeaders()
      });
      if (weekendRes.ok) {
        const data = await weekendRes.json();
        setWeekendEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error loading newsletter data:', error);
      toast.error('Failed to load newsletter data');
    } finally {
      setLoading(false);
    }
  };

  // Custom Mailing List functions
  const openNewListDialog = () => {
    setEditingList(null);
    setCustomListForm({ name: '', description: '', emails_text: '' });
    setCustomListDialog(true);
  };

  const openEditListDialog = async (list) => {
    // Fetch full list details
    try {
      const res = await fetch(`${API_URL}/api/newsletter/admin/custom-lists/${list.list_id}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const fullList = await res.json();
        setEditingList(fullList);
        // Convert emails array to text format
        const emailsText = (fullList.emails || [])
          .map(e => e.name ? `${e.email}, ${e.name}` : e.email)
          .join('\n');
        setCustomListForm({
          name: fullList.name,
          description: fullList.description || '',
          emails_text: emailsText
        });
        setCustomListDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load list details');
    }
  };

  const handleSaveCustomList = async () => {
    if (!customListForm.name.trim()) {
      toast.error('Please enter a list name');
      return;
    }
    if (!customListForm.emails_text.trim()) {
      toast.error('Please add at least one email');
      return;
    }

    setSavingList(true);
    try {
      // Parse emails text into array
      const emails = customListForm.emails_text
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
          const parts = line.split(',').map(p => p.trim());
          return {
            email: parts[0],
            name: parts[1] || ''
          };
        });

      const url = editingList
        ? `${API_URL}/api/newsletter/admin/custom-lists/${editingList.list_id}`
        : `${API_URL}/api/newsletter/admin/custom-lists`;
      
      const res = await fetch(url, {
        method: editingList ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: customListForm.name,
          description: customListForm.description,
          emails
        })
      });

      if (res.ok) {
        toast.success(editingList ? 'List updated!' : 'List created!');
        setCustomListDialog(false);
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save list');
      }
    } catch (error) {
      toast.error('Failed to save list');
    } finally {
      setSavingList(false);
    }
  };

  const handleDeleteCustomList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/newsletter/admin/custom-lists/${listId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('List deleted');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to delete list');
    }
  };

  // Visual Editor functions - Smart placeholder detection
  const detectPlaceholders = (html) => {
    // Find all [placeholder] patterns in the HTML
    const regex = /\[([^\]]+)\]/g;
    const found = new Set();
    let match;
    while ((match = regex.exec(html)) !== null) {
      // Skip merge tags like {{name}}
      if (!match[0].includes('{')) {
        found.add(match[0]);
      }
    }
    return Array.from(found);
  };

  const toggleVisualEditor = () => {
    if (!visualEditorMode) {
      // Entering visual editor - detect placeholders
      const placeholders = detectPlaceholders(composeForm.html_body);
      setDetectedPlaceholders(placeholders);
      // Initialize values object
      const initialValues = {};
      placeholders.forEach(p => {
        initialValues[p] = '';
      });
      setPlaceholderValues(initialValues);
    }
    setVisualEditorMode(!visualEditorMode);
  };

  const applyPlaceholderValues = () => {
    let html = composeForm.html_body;
    
    // Replace each placeholder with its value
    for (const [placeholder, value] of Object.entries(placeholderValues)) {
      if (value && value.trim()) {
        // Escape special regex characters in placeholder
        const escaped = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        html = html.replace(new RegExp(escaped, 'g'), value);
      }
    }
    
    setComposeForm(prev => ({ ...prev, html_body: html }));
    setVisualEditorMode(false);
    toast.success('Content updated!');
  };

  // Weekend Event Campaign functions
  const handleWeekendEventChange = async (eventId) => {
    setSelectedWeekendEvent(eventId);
    if (eventId && eventId !== 'none') {
      try {
        const res = await fetch(`${API_URL}/api/newsletter/admin/weekend-events/${eventId}/recipients`, {
          headers: getAuthHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          setWeekendRecipientCount(data.recipient_count || 0);
        }
      } catch (error) {
        console.error('Error fetching weekend recipients:', error);
      }
    } else {
      setWeekendRecipientCount(0);
    }
  };

  const handleSendWeekendCampaign = async () => {
    if (!composeForm.name || !composeForm.subject || !composeForm.html_body) {
      toast.error('Please fill in campaign name, subject, and HTML content');
      return;
    }
    if (!selectedWeekendEvent || selectedWeekendEvent === 'none') {
      toast.error('Please select a weekend event');
      return;
    }

    setSendingWeekendCampaign(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/admin/weekend-campaign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: composeForm.name,
          subject: composeForm.subject,
          html_body: composeForm.html_body,
          event_id: selectedWeekendEvent,
          include_dyslexia_pdf: composeForm.include_dyslexia_pdf
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Campaign sent to ${data.sent_count} recipients!`);
        loadData();
        // Reset form
        setComposeForm({
          name: '',
          subject: '',
          html_body: '',
          recipient_list_id: '',
          manual_recipients: '',
          schedule: false,
          scheduled_at: '',
          include_dyslexia_pdf: false
        });
        setSelectedWeekendEvent('');
        setWeekendRecipientCount(0);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send campaign');
      }
    } catch (error) {
      toast.error('Failed to send campaign');
    } finally {
      setSendingWeekendCampaign(false);
    }
  };

  // Individual email send (for verified financial information)
  const handleSendIndividualEmail = async () => {
    if (!composeForm.subject || !composeForm.html_body) {
      toast.error('Please fill in subject and HTML content');
      return;
    }
    if (!individualRecipient.email) {
      toast.error('Please enter recipient email');
      return;
    }

    setSendingIndividual(true);
    try {
      // Replace all placeholders with the individual values
      let personalizedHtml = composeForm.html_body;
      let personalizedSubject = composeForm.subject;
      
      // Replace merge tags
      const replacements = {
        '{{name}}': individualRecipient.name || '',
        '{{first_name}}': individualRecipient.first_name || individualRecipient.name?.split(' ')[0] || '',
        '{{email}}': individualRecipient.email,
        '{{booking_reference}}': individualRecipient.booking_reference || '',
        '{{booking_ref}}': individualRecipient.booking_reference || '',
        '{{reference}}': individualRecipient.booking_reference || '',
        // Square bracket placeholders for Easy Editor fields
        '[BOOKING REFERENCE]': individualRecipient.booking_reference || '',
        '[BOOKING TOTAL]': individualRecipient.booking_total || '',
        '[DEPOSIT PAID]': individualRecipient.deposit_paid || '',
        '[INSTALMENTS PAID]': individualRecipient.instalments_paid || '',
        '[BALANCE REMAINING]': individualRecipient.balance_remaining || '',
        '[PAYMENT DEADLINE]': individualRecipient.payment_deadline || '',
        '[BOOKING INCLUDES]': individualRecipient.booking_includes || ''
      };
      
      for (const [tag, value] of Object.entries(replacements)) {
        personalizedHtml = personalizedHtml.split(tag).join(value);
        personalizedSubject = personalizedSubject.split(tag).join(value);
      }

      const res = await fetch(`${API_URL}/api/newsletter/admin/send-individual`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          to_email: individualRecipient.email,
          subject: personalizedSubject,
          html_body: personalizedHtml,
          include_dyslexia_pdf: composeForm.include_dyslexia_pdf,
          recipient_name: individualRecipient.name
        })
      });

      if (res.ok) {
        toast.success(`Email sent to ${individualRecipient.email}`);
        // Clear recipient fields but keep template
        setIndividualRecipient({
          email: '',
          name: '',
          first_name: '',
          booking_reference: '',
          booking_total: '',
          deposit_paid: '',
          instalments_paid: '',
          balance_remaining: '',
          payment_deadline: '',
          booking_includes: ''
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingIndividual(false);
    }
  };

  // Preview individual email before sending
  const handlePreviewIndividual = () => {
    if (!composeForm.html_body) {
      toast.error('Please add HTML content first');
      return;
    }
    
    let previewContent = composeForm.html_body;
    
    const replacements = {
      '{{name}}': individualRecipient.name || '[Name]',
      '{{first_name}}': individualRecipient.first_name || individualRecipient.name?.split(' ')[0] || '[First Name]',
      '{{email}}': individualRecipient.email || '[Email]',
      '{{booking_reference}}': individualRecipient.booking_reference || '[Booking Reference]',
      '{{booking_ref}}': individualRecipient.booking_reference || '[Booking Reference]',
      '{{reference}}': individualRecipient.booking_reference || '[Booking Reference]',
      '[BOOKING REFERENCE]': individualRecipient.booking_reference || '[Booking Reference]',
      '[BOOKING TOTAL]': individualRecipient.booking_total || '[Booking Total]',
      '[DEPOSIT PAID]': individualRecipient.deposit_paid || '[Deposit Paid]',
      '[INSTALMENTS PAID]': individualRecipient.instalments_paid || '[Instalments Paid]',
      '[BALANCE REMAINING]': individualRecipient.balance_remaining || '[Balance Remaining]',
      '[PAYMENT DEADLINE]': individualRecipient.payment_deadline || '[Payment Deadline]',
      '[BOOKING INCLUDES]': individualRecipient.booking_includes || '[Booking Includes]'
    };
    
    for (const [tag, value] of Object.entries(replacements)) {
      previewContent = previewContent.split(tag).join(value);
    }
    
    setPreviewHtml(previewContent);
    setPreviewDialog(true);
  };

  // Template functions
  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.html_body) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingTemplate(true);
    try {
      const url = selectedTemplate 
        ? `${API_URL}/api/newsletter/admin/templates/${selectedTemplate.template_id}`
        : `${API_URL}/api/newsletter/admin/templates`;
      
      const res = await fetch(url, {
        method: selectedTemplate ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(templateForm)
      });

      if (res.ok) {
        toast.success(selectedTemplate ? 'Template updated' : 'Template created');
        setTemplateDialog(false);
        setSelectedTemplate(null);
        setTemplateForm({ name: '', subject: '', html_body: '', category: 'general' });
        loadData();
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const res = await fetch(`${API_URL}/api/newsletter/admin/templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Template deleted');
        loadData();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const loadTemplateIntoCompose = (template) => {
    setComposeForm(prev => ({
      ...prev,
      subject: template.subject,
      html_body: template.html_body
    }));
    setActiveView('compose');
    toast.success('Template loaded into composer');
  };

  // Preview
  const handlePreview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/newsletter/admin/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          subject: composeForm.subject,
          html_body: composeForm.html_body
        })
      });

      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.html_body);
        setPreviewDialog(true);
      }
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/admin/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          subject: composeForm.subject,
          html_body: composeForm.html_body,
          test_email: testEmail
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.test_sent) {
          toast.success(`Test email sent to ${testEmail}`);
        } else {
          toast.error('Failed to send test email');
        }
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // Send campaign
  const handleSendCampaign = async () => {
    if (!composeForm.name || !composeForm.subject || !composeForm.html_body) {
      toast.error('Please fill in campaign name, subject, and content');
      return;
    }

    if (!composeForm.recipient_list_id && !composeForm.manual_recipients) {
      toast.error('Please select a recipient list or add manual recipients');
      return;
    }

    setSending(true);
    try {
      const payload = {
        name: composeForm.name,
        subject: composeForm.subject,
        html_body: composeForm.html_body,
        recipient_list_id: composeForm.recipient_list_id && composeForm.recipient_list_id !== 'none' ? composeForm.recipient_list_id : null,
        manual_recipients: composeForm.manual_recipients 
          ? composeForm.manual_recipients.split('\n').map(e => e.trim()).filter(Boolean)
          : null,
        scheduled_at: composeForm.schedule && composeForm.scheduled_at 
          ? new Date(composeForm.scheduled_at).toISOString()
          : null,
        include_dyslexia_pdf: composeForm.include_dyslexia_pdf
      };

      const res = await fetch(`${API_URL}/api/newsletter/admin/campaigns`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        if (data.status === 'scheduled') {
          toast.success(`Campaign scheduled for ${new Date(data.scheduled_at).toLocaleString()}`);
        } else {
          toast.success(`Campaign sending to ${data.recipient_count} recipients`);
        }
        
        // Reset form
        setComposeForm({
          name: '',
          subject: '',
          html_body: '',
          recipient_list_id: '',
          manual_recipients: '',
          schedule: false,
          scheduled_at: '',
          include_dyslexia_pdf: false
        });
        loadData();
        setActiveView('campaigns');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send campaign');
      }
    } catch (error) {
      toast.error('Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sending':
        return <Badge className="bg-blue-100 text-blue-700"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sending</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'completed_with_errors':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />Partial</Badge>;
      case 'scheduled':
        return <Badge className="bg-purple-100 text-purple-700"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Newsletter & Campaigns</h2>
          <p className="text-sm text-slate-500">Compose and send HTML emails to members and guests</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'compose', label: 'Compose', icon: Edit },
          { id: 'templates', label: 'Templates', icon: FileText },
          { id: 'campaigns', label: 'Campaigns', icon: Send },
          { id: 'lists', label: 'Mailing Lists', icon: Users }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeView === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveView(tab.id)}
            className="gap-1"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === 'campaigns' && campaigns.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{campaigns.length}</Badge>
            )}
            {tab.id === 'lists' && customLists.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{customLists.length}</Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Compose View */}
      {activeView === 'compose' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Email Composer</CardTitle>
                <CardDescription>Create your email content. Use merge tags: {"{{name}}"}, {"{{first_name}}"}, {"{{email}}"}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Campaign Name *</Label>
                  <Input
                    value={composeForm.name}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., July Newsletter, Event Announcement"
                  />
                </div>

                <div>
                  <Label>Subject Line *</Label>
                  <Input
                    value={composeForm.subject}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="e.g., Line Dance Central Newsletter - July 2027"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>HTML Content *</Label>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={toggleVisualEditor}
                      className="text-xs"
                    >
                      {visualEditorMode ? 'Switch to HTML' : '✨ Easy Editor'}
                    </Button>
                  </div>
                  
                  {visualEditorMode ? (
                    <div className="space-y-3 p-4 border rounded-lg bg-slate-50">
                      {detectedPlaceholders.length > 0 ? (
                        <>
                          <p className="text-xs text-slate-500 mb-3">
                            We found {detectedPlaceholders.length} placeholder(s) in your template. Fill in the values below and click &quot;Apply&quot; to replace them.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {detectedPlaceholders.map((placeholder) => (
                              <div key={placeholder}>
                                <Label className="text-xs font-medium text-slate-700">
                                  {placeholder}
                                </Label>
                                <Input
                                  value={placeholderValues[placeholder] || ''}
                                  onChange={(e) => setPlaceholderValues(prev => ({ 
                                    ...prev, 
                                    [placeholder]: e.target.value 
                                  }))}
                                  placeholder={`Enter value for ${placeholder}`}
                                  className="mt-1"
                                />
                              </div>
                            ))}
                          </div>
                          <Button onClick={applyPlaceholderValues} className="w-full mt-4">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Apply to Template
                          </Button>
                        </>
                      ) : (
                        <div className="text-center py-6 text-slate-500">
                          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                          <p className="text-sm">No placeholders found in your template.</p>
                          <p className="text-xs mt-1">Add text like <code className="bg-slate-200 px-1 rounded">[Event Name]</code> or <code className="bg-slate-200 px-1 rounded">[Date]</code> to your HTML to use the Easy Editor.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Textarea
                      value={composeForm.html_body}
                      onChange={(e) => setComposeForm(prev => ({ ...prev, html_body: e.target.value }))}
                      placeholder="Paste your HTML email content here..."
                      className="min-h-[300px] font-mono text-sm"
                    />
                  )}
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handlePreview} disabled={!composeForm.html_body}>
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {/* Recipients */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recipients</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Select List</Label>
                  <Select 
                    value={composeForm.recipient_list_id} 
                    onValueChange={(v) => setComposeForm(prev => ({ ...prev, recipient_list_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a list..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {recipientLists.map(list => (
                        <SelectItem key={list.id} value={list.id}>
                          {list.name} ({list.count})
                        </SelectItem>
                      ))}
                      {customLists.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-100">Custom Lists</div>
                          {customLists.map(list => (
                            <SelectItem key={`custom_${list.list_id}`} value={`custom_${list.list_id}`}>
                              📋 {list.name} ({list.email_count})
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Or Add Manual Recipients</Label>
                  <Textarea
                    value={composeForm.manual_recipients}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, manual_recipients: e.target.value }))}
                    placeholder="Enter emails, one per line..."
                    className="min-h-[100px] text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">One email per line</p>
                </div>
              </CardContent>
            </Card>

            {/* Weekend Event Campaign */}
            <Card className="border-violet-200 bg-violet-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-violet-600" />
                  Weekend Event Email
                </CardTitle>
                <CardDescription className="text-xs">
                  Send to all guests of a specific weekend with their booking reference
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-sm">Select Weekend Event</Label>
                  <Select 
                    value={selectedWeekendEvent} 
                    onValueChange={handleWeekendEventChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose event..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {weekendEvents.map(event => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.title} ({event.booking_count} bookings)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedWeekendEvent && selectedWeekendEvent !== 'none' && (
                  <>
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-sm">
                        <span className="font-medium">{weekendRecipientCount}</span> recipients will receive this email
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Includes lead guests + secondary guests with emails
                      </p>
                    </div>
                    
                    <div className="bg-violet-100 p-3 rounded-lg">
                      <p className="text-xs font-medium text-violet-700 mb-1">Available merge tags:</p>
                      <code className="text-xs text-violet-600">{"{{booking_reference}}"} {"{{name}}"} {"{{first_name}}"}</code>
                    </div>
                    
                    <Button 
                      className="w-full bg-violet-600 hover:bg-violet-700" 
                      onClick={handleSendWeekendCampaign}
                      disabled={sendingWeekendCampaign || !composeForm.name || !composeForm.subject || !composeForm.html_body}
                    >
                      {sendingWeekendCampaign ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send to Weekend Guests
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Individual Send - For Verified Financial Emails */}
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-emerald-600" />
                  Send to Individual
                </CardTitle>
                <CardDescription className="text-xs">
                  Send one email at a time with verified financial information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Switch
                    checked={individualMode}
                    onCheckedChange={setIndividualMode}
                  />
                  <Label className="text-sm font-medium">Enable Individual Send Mode</Label>
                </div>
                
                {individualMode && (
                  <>
                    <div className="bg-white p-3 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-emerald-700 mb-2">Recipient Details</p>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Email *</Label>
                          <Input
                            value={individualRecipient.email}
                            onChange={(e) => setIndividualRecipient(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="guest@email.com"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Full Name</Label>
                          <Input
                            value={individualRecipient.name}
                            onChange={(e) => setIndividualRecipient(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="John Smith"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs">Booking Reference</Label>
                        <Input
                          value={individualRecipient.booking_reference}
                          onChange={(e) => setIndividualRecipient(prev => ({ ...prev, booking_reference: e.target.value }))}
                          placeholder="WKD-001"
                          className="text-sm"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs">Booking Includes</Label>
                        <Input
                          value={individualRecipient.booking_includes}
                          onChange={(e) => setIndividualRecipient(prev => ({ ...prev, booking_includes: e.target.value }))}
                          placeholder="Weekend accommodation and return coach travel"
                          className="text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="bg-white p-3 rounded-lg border space-y-3">
                      <p className="text-xs font-medium text-emerald-700 mb-2">Financial Information</p>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Booking Total</Label>
                          <Input
                            value={individualRecipient.booking_total}
                            onChange={(e) => setIndividualRecipient(prev => ({ ...prev, booking_total: e.target.value }))}
                            placeholder="250.00"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Deposit Paid</Label>
                          <Input
                            value={individualRecipient.deposit_paid}
                            onChange={(e) => setIndividualRecipient(prev => ({ ...prev, deposit_paid: e.target.value }))}
                            placeholder="50.00"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Instalments Paid</Label>
                          <Input
                            value={individualRecipient.instalments_paid}
                            onChange={(e) => setIndividualRecipient(prev => ({ ...prev, instalments_paid: e.target.value }))}
                            placeholder="15.00"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Balance Remaining</Label>
                          <Input
                            value={individualRecipient.balance_remaining}
                            onChange={(e) => setIndividualRecipient(prev => ({ ...prev, balance_remaining: e.target.value }))}
                            placeholder="185.00"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs">Payment Deadline</Label>
                        <Input
                          value={individualRecipient.payment_deadline}
                          onChange={(e) => setIndividualRecipient(prev => ({ ...prev, payment_deadline: e.target.value }))}
                          placeholder="Monday 1st February 2027"
                          className="text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={handlePreviewIndividual}
                        disabled={!composeForm.html_body}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                      </Button>
                      <Button 
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={handleSendIndividualEmail}
                        disabled={sendingIndividual || !composeForm.subject || !composeForm.html_body || !individualRecipient.email}
                      >
                        {sendingIndividual ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Email
                      </Button>
                    </div>
                    
                    <p className="text-xs text-emerald-600 text-center">
                      Placeholders like [BOOKING TOTAL] and {"{{name}}"} will be replaced with values above
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Scheduling & Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={composeForm.schedule}
                    onCheckedChange={(v) => setComposeForm(prev => ({ ...prev, schedule: v }))}
                  />
                  <Label>Schedule for later</Label>
                </div>

                {composeForm.schedule && (
                  <div>
                    <Label>Send Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={composeForm.scheduled_at}
                      onChange={(e) => setComposeForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                    />
                  </div>
                )}
                
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={composeForm.include_dyslexia_pdf}
                      onCheckedChange={(v) => setComposeForm(prev => ({ ...prev, include_dyslexia_pdf: v }))}
                    />
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-violet-600" />
                      Attach dyslexia-friendly PDF
                    </Label>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-9">
                    Attach a printable PDF with larger fonts, increased spacing, and dyslexia-friendly formatting
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Send Button */}
            <Button 
              className="w-full" 
              size="lg"
              onClick={handleSendCampaign}
              disabled={sending}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : composeForm.schedule ? (
                <Clock className="w-4 h-4 mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {composeForm.schedule ? 'Schedule Campaign' : 'Send Now'}
            </Button>

            {/* Quick Templates */}
            {templates.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Quick Load Template</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {templates.slice(0, 3).map(template => (
                      <Button
                        key={template.template_id}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-left"
                        onClick={() => loadTemplateIntoCompose(template)}
                      >
                        <FileText className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="truncate">{template.name}</span>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Templates View */}
      {activeView === 'templates' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-base">Email Templates</CardTitle>
                <CardDescription>Reusable templates for your campaigns</CardDescription>
              </div>
              <Button onClick={() => {
                setSelectedTemplate(null);
                setTemplateForm({ name: '', subject: '', html_body: '', category: 'general' });
                setTemplateDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No templates yet</p>
                <p className="text-sm">Create your first email template</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(template => (
                    <TableRow key={template.template_id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-xs truncate">{template.subject}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{template.category}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(template.updated_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadTemplateIntoCompose(template)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTemplate(template);
                              setTemplateForm({
                                name: template.name,
                                subject: template.subject,
                                html_body: template.html_body,
                                category: template.category
                              });
                              setTemplateDialog(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteTemplate(template.template_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Campaigns View */}
      {activeView === 'campaigns' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Campaign History</CardTitle>
            <CardDescription>Past and scheduled email campaigns</CardDescription>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Send className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No campaigns yet</p>
                <p className="text-sm">Campaigns will appear here after sending</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map(campaign => (
                    <TableRow key={campaign.campaign_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          <p className="text-sm text-slate-500 truncate max-w-xs">{campaign.subject}</p>
                        </div>
                      </TableCell>
                      <TableCell>{campaign.recipient_count}</TableCell>
                      <TableCell>
                        {campaign.sent_count > 0 && (
                          <span className="text-green-600">{campaign.sent_count} sent</span>
                        )}
                        {campaign.failed_count > 0 && (
                          <span className="text-red-600 ml-2">{campaign.failed_count} failed</span>
                        )}
                        {!campaign.sent_count && !campaign.failed_count && '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {campaign.scheduled_at ? formatDate(campaign.scheduled_at) : formatDate(campaign.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mailing Lists View */}
      {activeView === 'lists' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Custom Mailing Lists</CardTitle>
              <CardDescription>Create and manage your own mailing lists</CardDescription>
            </div>
            <Button onClick={openNewListDialog}>
              <Plus className="w-4 h-4 mr-2" />
              New List
            </Button>
          </CardHeader>
          <CardContent>
            {customLists.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No custom lists yet</p>
                <p className="text-sm">Create a list to save groups of email addresses</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Emails</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customLists.map(list => (
                    <TableRow key={list.list_id}>
                      <TableCell className="font-medium">{list.name}</TableCell>
                      <TableCell className="text-slate-500">{list.description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{list.email_count} emails</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(list.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditListDialog(list)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteCustomList(list.list_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Custom List Dialog */}
      <Dialog open={customListDialog} onOpenChange={setCustomListDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingList ? 'Edit Mailing List' : 'Create Mailing List'}</DialogTitle>
            <DialogDescription>Add emails to your custom list</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>List Name *</Label>
              <Input
                value={customListForm.name}
                onChange={(e) => setCustomListForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., VIP Members, Workshop Attendees"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={customListForm.description}
                onChange={(e) => setCustomListForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description..."
              />
            </div>
            
            <div>
              <Label>Email Addresses *</Label>
              <Textarea
                value={customListForm.emails_text}
                onChange={(e) => setCustomListForm(prev => ({ ...prev, emails_text: e.target.value }))}
                placeholder="Enter one email per line, optionally with name:
john@example.com, John Smith
jane@example.com, Jane Doe
bob@example.com"
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">Format: email@example.com or email@example.com, Name</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomListDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCustomList} disabled={savingList}>
              {savingList && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {editingList ? 'Update List' : 'Create List'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Monthly Newsletter"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={templateForm.category}
                  onValueChange={(v) => setTemplateForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="newsletter">Newsletter</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Subject Line *</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject..."
              />
            </div>

            <div>
              <Label>HTML Content *</Label>
              <Textarea
                value={templateForm.html_body}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, html_body: e.target.value }))}
                placeholder="Paste HTML content..."
                className="min-h-[300px] font-mono text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
              {savingTemplate && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>This is how your email will look (with sample merge tags)</DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-lg overflow-hidden bg-white">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[400px]"
              title="Email Preview"
            />
          </div>

          <div className="flex items-center gap-2 pt-4 border-t">
            <Input
              placeholder="Send test to email..."
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSendTest} disabled={sendingTest}>
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminNewsletterTab;
